// src/components/Blog/BlogSection.jsx
// Literal markup match of the reference's .a-blog-columns/.a-blog-column
// (--tech)/.a-blog-column-head/.a-blog-list/.a-blog-card/.a-blog-thumb/
// .a-blog-card-body/.a-blog-meta/.a-blog-read — the "Blog preview section"
// appended near the end of the reference file. Rendered inside
// HomeTemplate's <section class="a-section a-section--blogs" id="blogs">.
// Real data only (fetchPosts/fetchTechArticles → GET /posts, GET /tech),
// following the same fetch-on-mount / skeleton / empty-state-hide pattern
// already used by EpisodeRail and FeaturedAdsSection.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchPosts, fetchTechArticles } from '../../api';
import { resolveImageUrl } from '../../utils/imageUrl';
import './blog-section.css';

const COLUMN_LIMIT = 2; // reference shows exactly 2 cards per column

// The reference's kicker/meta labels ("Culture & Commentary", "Culture · 6
// min read") are editorial flavor text — there's no `category` field on the
// real Post/Tech serializers, only `reading_time`. Per-column static kicker
// + label combined with the real reading_time keeps the reference's exact
// copy pattern without inventing a fake per-post category.
const COLUMN_META = {
  blog: { kicker: 'Culture & Commentary', metaLabel: 'Culture' },
  tech: { kicker: 'Devices & Digital Life', metaLabel: 'Technology' },
};

// `is_locked`/`access_required` come pre-computed from the backend
// (SubscriptionAccessService) — this card only renders what it's told, it
// never decides access itself.
const BlogCard = ({ item, basePath, metaLabel }) => {
  const locked = item.is_locked;
  const href = `${basePath}/${item.slug}`;
  const cover = resolveImageUrl(item.cover_image_url || item.cover_image);
  const readTime = item.reading_time ? `${item.reading_time} min read` : '';

  return (
    <Link className="a-blog-card" to={href}>
      <div className="a-blog-thumb" style={cover ? { backgroundImage: `url(${cover})` } : undefined}>
        {locked && (
          <span className="a-blog-thumb__lock" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M7 10V7a5 5 0 0 1 10 0v3M5 10h14v11H5zM12 14v3" />
            </svg>
          </span>
        )}
      </div>
      <div className="a-blog-card-body">
        <div className="a-blog-meta">
          {metaLabel}
          {readTime ? ` · ${readTime}` : ''}
        </div>
        <h4>{item.title}</h4>
        {item.excerpt && <p className={locked ? 'is-locked' : ''}>{item.excerpt}</p>}
        <span className="a-blog-read">{locked ? 'Unlock with membership →' : 'Read article →'}</span>
      </div>
    </Link>
  );
};

const BlogColumn = ({ variant, title, seeAllHref, items, loading, basePath }) => {
  if (!loading && items.length === 0) {
    return null;
  }

  const { kicker, metaLabel } = COLUMN_META[variant];

  return (
    <section className={`a-blog-column ${variant === 'tech' ? 'a-blog-column--tech' : ''}`}>
      <div className="a-blog-column-head">
        <div>
          <small>{kicker}</small>
          <h3>{title}</h3>
        </div>
        <Link to={seeAllHref}>View blog →</Link>
      </div>
      <div className="a-blog-list">
        {loading
          ? Array.from({ length: COLUMN_LIMIT }).map((_, i) => (
              <div className="a-blog-card a-blog-card--skeleton" key={i} />
            ))
          : items.map((item) => (
              <BlogCard key={item.id} item={item} basePath={basePath} metaLabel={metaLabel} />
            ))}
      </div>
    </section>
  );
};

const BlogSection = () => {
  const [posts, setPosts] = useState([]);
  const [techs, setTechs] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [loadingTechs, setLoadingTechs] = useState(true);

  useEffect(() => {
    let isMounted = true;
    fetchPosts()
      .then((data) => {
        if (!isMounted) return;
        const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        setPosts(list.slice(0, COLUMN_LIMIT));
      })
      .catch((err) => {
        console.error('Failed to load It Guhso posts:', err);
      })
      .finally(() => {
        if (isMounted) setLoadingPosts(false);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    fetchTechArticles()
      .then((data) => {
        if (!isMounted) return;
        const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        setTechs(list.slice(0, COLUMN_LIMIT));
      })
      .catch((err) => {
        console.error('Failed to load Tech articles:', err);
      })
      .finally(() => {
        if (isMounted) setLoadingTechs(false);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  // Match EpisodeRail/FeaturedAdsSection convention: hide the whole section
  // only once both fetches have resolved and both are genuinely empty.
  if (!loadingPosts && !loadingTechs && posts.length === 0 && techs.length === 0) {
    return null;
  }

  return (
    <>
      <div className="a-rail-label">
        <h3>From the Guhso Blogs</h3>
        <a href="#explore">Explore all →</a>
      </div>
      <div className="a-blog-columns">
        <BlogColumn
          variant="blog"
          title="It Guhso"
          seeAllHref="/blog"
          items={posts}
          loading={loadingPosts}
          basePath="/blog"
        />
        <BlogColumn
          variant="tech"
          title="Tech"
          seeAllHref="/tech"
          items={techs}
          loading={loadingTechs}
          basePath="/tech"
        />
      </div>
    </>
  );
};

export default BlogSection;
